import { Injectable } from '@angular/core';
import { Observable, delay, of, switchMap, throwError, timer } from 'rxjs';

import { PaymentsDashboardRepository } from '@app/domain/ports/payments-dashboard.repository';
import { PaymentsDashboardError } from '@app/domain/models/payments-dashboard/payments-dashboard.error';
import { aFechaLocal } from '@app/domain/models/payments-dashboard/payments-dashboard-rules';
import type {
  PaymentsAmountCount,
  PaymentsDashboardData,
  PaymentsDashboardPeriod,
  PaymentsDashboardQuery,
  PaymentsSeriePoint,
} from '@app/domain/models/payments-dashboard/payments-dashboard.model';

const MOCK_DELAY_MS = 300;

const dd = (n: number) => String(n).padStart(2, '0');

/** Semana ISO (`YYYY-Www`) de una fecha local: la semana es la de su jueves. */
function semanaIso(d: Date): string {
  const jueves = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  jueves.setDate(jueves.getDate() - ((jueves.getDay() + 6) % 7) + 3);
  const anioIso = jueves.getFullYear();
  const primerJueves = new Date(anioIso, 0, 4);
  primerJueves.setDate(primerJueves.getDate() - ((primerJueves.getDay() + 6) % 7) + 3);
  const semana = 1 + Math.round((jueves.getTime() - primerJueves.getTime()) / (7 * 86400000));
  return `${anioIso}-W${dd(semana)}`;
}

function lunesDe(d: Date): Date {
  const lunes = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  lunes.setDate(lunes.getDate() - ((lunes.getDay() + 6) % 7));
  return lunes;
}

/**
 * Eje de demostración con la MISMA forma que el back (`movements/series.py`):
 * completo con ceros para hoy/semana/mes; para año solo los meses con cobros.
 * Pura y con `hoy` inyectado, para que su prueba no dependa del reloj.
 */
export function ejeDeDemostracion(period: Exclude<PaymentsDashboardPeriod, 'custom'>, hoy: Date): string[] {
  const y = hoy.getFullYear();
  const m = hoy.getMonth();
  switch (period) {
    case 'hoy':
      // 8 tramos de 3 h.
      return Array.from({ length: 8 }, (_, i) => `${aFechaLocal(hoy)}T${dd(i * 3)}:00`);
    case 'semana': {
      const lunes = lunesDe(hoy);
      return Array.from({ length: 7 }, (_, i) => {
        const dia = new Date(lunes);
        dia.setDate(lunes.getDate() + i);
        return aFechaLocal(dia);
      });
    }
    case 'mes': {
      const semanas: string[] = [];
      const ultimo = new Date(y, m + 1, 0).getDate();
      for (let dia = 1; dia <= ultimo; dia++) {
        const s = semanaIso(new Date(y, m, dia));
        if (!semanas.includes(s)) semanas.push(s);
      }
      return semanas;
    }
    case 'año':
      // Eje DISPERSO: solo los meses con cobros (enero, marzo, junio y el actual).
      return [...new Set([0, 2, 5, m].filter((mes) => mes <= m))].map((mes) => `${y}-${dd(mes + 1)}`);
  }
}

/** Valores deterministas por posición (sin `Math.random`): unos ceros y un monto nulo. */
function puntoDeDemostracion(bucket: string, i: number): PaymentsSeriePoint {
  const linkCount = (i * 3 + 1) % 5;
  const chargeCount = (i * 2) % 3;
  const qrCount = (i + 2) % 4;
  const monto = (conteo: number, base: number): number => Math.round(conteo * base * 100) / 100;
  return {
    bucket,
    link: { count: linkCount, amount: monto(linkCount, 450.5) },
    // Un bucket con cargos cuyo monto viene nulo: la vista lo pinta "—".
    charge: { count: chargeCount, amount: i === 1 && chargeCount > 0 ? null : monto(chargeCount, 1200) },
    qr: { count: qrCount, amount: monto(qrCount, 85.25) },
  };
}

const QR_DE_DEMOSTRACION: Readonly<Record<string, PaymentsAmountCount>> = {
  Paid: { count: 18, amount: 4120.5 },
  Pending: { count: 4, amount: 860 },
  // `Reserved` sin monto: el caso "sin dato" que la vista pinta "—".
  Reserved: { count: 2, amount: null },
  Cancelled: { count: 3, amount: 510 },
  Expired: { count: 5, amount: 990.75 },
};

function rangoDeDemostracion(period: PaymentsDashboardPeriod, hoy: Date): { from: string; to: string } {
  const to = aFechaLocal(hoy);
  switch (period) {
    case 'semana':
      return { from: aFechaLocal(lunesDe(hoy)), to };
    case 'mes':
      return { from: aFechaLocal(new Date(hoy.getFullYear(), hoy.getMonth(), 1)), to };
    case 'año':
      return { from: aFechaLocal(new Date(hoy.getFullYear(), 0, 1)), to };
    default:
      return { from: to, to };
  }
}

/** Respuesta de demostración del periodo pedido. Pura (`hoy` inyectado). */
export function datosDeDemostracion(query: PaymentsDashboardQuery, hoy: Date): PaymentsDashboardData {
  if (query.period === 'custom') {
    const from = query.dateFrom ?? '';
    const to = query.dateTo ?? '';
    // Un rango a medida solo trae los días con cobros: aquí, sus dos extremos.
    const buckets = [...new Set([from, to])];
    return {
      periodLabel: 'custom',
      from,
      to,
      serie: buckets.map(puntoDeDemostracion),
      qr: { MXN: QR_DE_DEMOSTRACION },
    };
  }
  const { from, to } = rangoDeDemostracion(query.period, hoy);
  return {
    periodLabel: query.period,
    from,
    to,
    serie: ejeDeDemostracion(query.period, hoy).map(puntoDeDemostracion),
    qr: { MXN: QR_DE_DEMOSTRACION },
  };
}

/**
 * Dashboard de pagos de DEMOSTRACIÓN (toggle `environment.paymentsDashboardUseMocks`).
 * Simula el retardo del back y su 400 de periodo inválido (`from > to` o custom
 * incompleto), para poder probar la UI sin el endpoint (hoy solo existe en stage).
 */
@Injectable()
export class PaymentsDashboardMockDataSource extends PaymentsDashboardRepository {
  override getDashboard(query: PaymentsDashboardQuery): Observable<PaymentsDashboardData> {
    if (
      query.period === 'custom' &&
      (!query.dateFrom || !query.dateTo || query.dateFrom > query.dateTo)
    ) {
      return timer(MOCK_DELAY_MS).pipe(switchMap(() => throwError(() => new PaymentsDashboardError('badRequest'))));
    }
    return of(datosDeDemostracion(query, new Date())).pipe(delay(MOCK_DELAY_MS));
  }
}
